/**
 * Copy go-live checklist proof photos into the public listing galleries (COL-529).
 *
 * Checklist photos are uploaded to the private `contracts` bucket (so an owner
 * can write them); the property/room galleries are the public `property-images`
 * bucket. "Attributing" a checklist photo to a gallery therefore copies the
 * bytes across, not a pointer.
 *
 * - The admin create path (useCreatePropertyV2) copies at creation time.
 * - Owner submissions can't write property-images, so their photos are copied
 *   at staff approval here (attributeOwnerChecklistPhotosToGallery).
 */
import { supabase } from "@/integrations/supabase/client";
import { fetchStoredFile } from "@/lib/storage-url";
import { CONTRACTS_BUCKET, uploadPropertyImage } from "@/lib/v2-upload";

/**
 * Copy private checklist proof photos into the public `property-images` gallery,
 * returning the new public URLs. Best-effort per file and parallel — a copy that
 * fails is skipped (reason pushed to `errors`) rather than failing the caller,
 * since the proof still lives on the checklist card. Admin-write only
 * (property-images is admin-write-only).
 */
export async function copyChecklistPhotosToGallery(
  paths: string[],
  errors: string[]
): Promise<string[]> {
  const copied = await Promise.all(
    paths.map(async (path): Promise<string | null> => {
      try {
        const resp = await fetchStoredFile(path, CONTRACTS_BUCKET);
        if (!resp.ok) throw new Error(`could not read proof (${resp.status})`);
        const blob = await resp.blob();
        const type = blob.type || "image/jpeg";
        const ext = type.split("/")[1] || "jpg";
        const { url } = await uploadPropertyImage(
          new File([blob], `checklist-photo.${ext}`, { type })
        );
        return url;
      } catch (e) {
        errors.push(
          `Gallery photo: ${e instanceof Error ? e.message : String(e)}`
        );
        return null;
      }
    })
  );
  return copied.filter((u): u is string => u != null);
}

/** Where a checklist photo slot (e.g. "outside:0", "common:1", "r2:3") belongs
 *  in the galleries: the building exterior and common area are property photos;
 *  "r{n}" is the n-th bedroom → the n-th room (by position). Anything else has
 *  no gallery home. Pure, so the mapping is unit-tested on its own. */
export function classifyPhotoSlot(
  slot: string
): { target: "property" } | { target: "room"; roomIndex: number } | null {
  const shot = slot.split(":")[0];
  if (shot === "outside" || shot === "common") return { target: "property" };
  const m = /^r(\d+)$/.exec(shot);
  if (m) return { target: "room", roomIndex: parseInt(m[1], 10) - 1 };
  return null;
}

/**
 * On staff approval of an owner submission, copy the owner's checklist proof
 * photos into the public galleries: exterior/common → the property gallery, each
 * bedroom's shots → that room (by position — r1 → the apartment's first room).
 * Runs as the approving staff member (owners can't write property-images).
 *
 * Best-effort: every failure is collected and returned (never thrown) so a copy
 * problem can't undo an approval that already committed. Returns the collected
 * error messages ([] on full success).
 */
export async function attributeOwnerChecklistPhotosToGallery(
  propertyId: number
): Promise<string[]> {
  const errors: string[] = [];
  const galleryPaths: string[] = []; // exterior/common, across all apartments

  const { data: apts, error: aErr } = await supabase
    .from("apartments")
    .select("id")
    .eq("property_id", propertyId)
    .order("id", { ascending: true });
  if (aErr) {
    errors.push(`apartments: ${aErr.message}`);
    return errors;
  }

  for (const apt of apts ?? []) {
    const aptId = apt.id as number;
    // The "photos" item holds the room/exterior shots (ware/washer are separate
    // proofs, not listing photos, so they're deliberately left out).
    const { data: items } = await supabase
      .from("apartment_checklist_items")
      .select("id")
      .eq("apartment_id", aptId)
      .eq("item_key", "photos");
    const photoItemId = items?.[0]?.id;
    if (!photoItemId) continue;

    const { data: files } = await supabase
      .from("apartment_checklist_files")
      .select("file_url, file_name")
      .eq("checklist_item_id", photoItemId);
    if (!files || files.length === 0) continue;

    const { data: rooms } = await supabase
      .from("rooms")
      .select("id")
      .eq("apartment_id", aptId)
      .order("id", { ascending: true });

    // Group this apartment's bedroom shots by their room; exterior/common fold
    // into the property-wide list.
    const roomPaths = new Map<number, string[]>();
    for (const f of files) {
      const path = f.file_url as string | null;
      if (!path) continue;
      const cls = classifyPhotoSlot((f.file_name as string) ?? "");
      if (!cls) continue;
      if (cls.target === "property") {
        galleryPaths.push(path);
      } else {
        const room = rooms?.[cls.roomIndex];
        if (!room) continue;
        const id = room.id as number;
        roomPaths.set(id, [...(roomPaths.get(id) ?? []), path]);
      }
    }

    for (const [roomId, paths] of roomPaths) {
      const urls = await copyChecklistPhotosToGallery(paths, errors);
      if (urls.length === 0) continue;
      const { data: room } = await supabase
        .from("rooms")
        .select("images")
        .eq("id", roomId)
        .single();
      const existing = ((room?.images as string[] | null) ?? []).filter(
        Boolean
      );
      const { error: rErr } = await supabase
        .from("rooms")
        .update({ images: [...existing, ...urls] })
        .eq("id", roomId);
      if (rErr) errors.push(`room ${roomId} photos: ${rErr.message}`);
    }
  }

  if (galleryPaths.length > 0) {
    const urls = await copyChecklistPhotosToGallery(galleryPaths, errors);
    if (urls.length > 0) {
      const { data: prop } = await supabase
        .from("properties")
        .select("images, image_url")
        .eq("id", propertyId)
        .single();
      const existing = ((prop?.images as string[] | null) ?? []).filter(
        Boolean
      );
      const { error: pErr } = await supabase
        .from("properties")
        .update({
          images: [...existing, ...urls],
          image_url:
            (prop?.image_url as string | null) ?? existing[0] ?? urls[0],
        })
        .eq("id", propertyId);
      if (pErr) errors.push(`property gallery: ${pErr.message}`);
    }
  }

  return errors;
}
